import { Accessibility, Calendar, CornerDownLeft, MapPin, Phone, StickyNote, Timer, User, Car, DollarSign } from "lucide-react";
import { fmtTime, returnLabel, statusClass, tripTypeLabel, THEME } from "@/lib/calendar";

const accessibilityItems = (r) => {
  const items = [...(r.mobility_aids || []), ...(r.assistance_needs || [])];
  if (r.mobility_aid && r.mobility_aid !== "none") items.push(r.mobility_aid_other || r.mobility_aid);
  if (r.accessibility_need && r.accessibility_need !== "none") items.push(r.accessibility_other || r.accessibility_need);
  if (r.needs_entry_assistance) items.push("Help getting in/out of vehicle");
  if (r.has_service_animal) items.push("Service animal");
  return items.map((i) => String(i).replace(/_/g, " "));
};

const Row = ({ icon: Icon, label, children, t, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${t.accent}`} />
    <div className="min-w-0">
      <p className={`text-[11px] uppercase tracking-wide font-semibold ${t.muted}`}>{label}</p>
      <div className="text-sm font-medium break-words">{children}</div>
    </div>
  </div>
);

export const RideInfo = ({ ride, variant = "light", showFare = true }) => {
  const t = THEME[variant];
  const ret = returnLabel(ride);
  const stops = (ride.stops || []).filter((s) => s.address);
  const access = accessibilityItems(ride);
  return (
    <div className="space-y-4" data-testid="ride-info">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(ride.status, variant)}`} data-testid="ride-info-status">{ride.status.replace("_", " ")}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${t.sub}`}>{ride.ride_type === "private" ? "Private Ride" : "NEMT"}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${t.sub}`} data-testid="ride-info-trip-type">{tripTypeLabel(ride)}</span>
        {ride.booking_source === "phone" && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Phone booking</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Row icon={User} label="Customer" t={t} testId="ride-info-customer">{ride.client_name}</Row>
        <Row icon={Phone} label="Phone" t={t}><a href={`tel:${ride.client_phone}`} className="underline">{ride.client_phone}</a></Row>
        <Row icon={Calendar} label="Pickup" t={t} testId="ride-info-pickup-time">{ride.pickup_date} at {fmtTime(ride.pickup_time)}</Row>
        {ret && <Row icon={CornerDownLeft} label="Return pickup" t={t} testId="ride-info-return-time">{ret}</Row>}
        <Row icon={MapPin} label="Pickup address" t={t}>{ride.pickup_locations?.[0]?.address}</Row>
        <Row icon={MapPin} label="Destination" t={t}>{ride.dropoff_address}</Row>
        {(ride.wait_and_return || ride.wait_time > 0) && <Row icon={Timer} label="Planned wait" t={t}>{ride.wait_time} minutes (Wait & Return)</Row>}
        <Row icon={Car} label="Driver / vehicle" t={t} testId="ride-info-driver">
          {ride.driver_name ? `${ride.driver_name}${ride.driver_phone ? ` · ${ride.driver_phone}` : ""}` : <span className="text-red-500 font-bold">No driver assigned</span>}
          <span className={`block text-xs ${t.muted}`}>{(ride.vehicle_type || "car").replace("_", " ")}</span>
        </Row>
        {showFare && ride.fare_estimate > 0 && (
          <Row icon={DollarSign} label="Fare / payment" t={t}>${Number(ride.fare_estimate).toFixed(2)} · {ride.payment_method || "—"}</Row>
        )}
      </div>
      {stops.length > 0 && (
        <div className={`rounded-xl border p-3 ${t.sub}`} data-testid="ride-info-stops">
          <p className={`text-[11px] uppercase tracking-wide font-semibold mb-2 ${t.muted}`}>Additional stops</p>
          <ol className="space-y-1 text-sm list-decimal list-inside">
            {stops.map((s, i) => <li key={i}>{s.address}{s.wait_minutes ? ` — wait ${s.wait_minutes} min` : ""}</li>)}
          </ol>
        </div>
      )}
      {access.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-amber-900" data-testid="ride-info-accessibility">
          <p className="text-[11px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1"><Accessibility className="w-3 h-3" /> Accessibility & assistance</p>
          <p className="text-sm capitalize">{access.join(", ")}</p>
        </div>
      )}
      {(ride.special_requests || ride.dispatch_notes) && (
        <Row icon={StickyNote} label="Trip notes" t={t}>
          {ride.special_requests && <p>{ride.special_requests}</p>}
          {ride.dispatch_notes && <p className={t.muted}>Dispatch: {ride.dispatch_notes}</p>}
        </Row>
      )}
      {ride.status === "cancelled" && (
        <p className="text-sm text-red-600 font-semibold">Cancelled{ride.cancel_reason ? `: ${ride.cancel_reason}` : ""}</p>
      )}
    </div>
  );
};

export default RideInfo;
